/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {BulkActionEntity} from '@ion/core/src/types/entities';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {BulkActionProps} from '@ion/dls/src/imports/BulkUploadContainer';
import {dlsColor} from '@ion/dls/src/theme/theme';
import BulkFileUpload from './BulkFileUpload';
import {BulkSelectEntitiesFormFields, parsePastedIds} from './BulkSelectEntitiesEditorUtils';

export interface EntityBulkUploadProps extends BulkActionProps {
  formState: FormState;
  setFormState: (newState: FormState) => void;
  entity: BulkActionEntity;
  handleChooserChange: Function;
  showFileOption?: boolean;
}

const useStyles = makeStyles(() => ({
  container : {
    display        : 'flex',
    justifyContent : 'space-between',
    margin         : '25px 0'
  },
  listContainer : {
    flex : 1
  },
  textFieldHeight : {
    height          : '216px',
    backgroundColor : dlsColor('white'),
    '&:after'       : {
      borderColor : dlsColor('caBlue')
    },
    '&:before' : {
      borderColor       : `${dlsColor('grey5')} !important`,
      borderBottomWidth : '1px !important'
    }
  },
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '16px',
    fontFamily : 'Roboto',
    fontWeight : 400
  }
}));

/**
 * This step will display the page for uploading or pasting identifiers.
 */
export const EntityBulkUpload = ({
  entity,
  formState,
  handleChooserChange,
  setFormState,
  isBulkAction = false,
  showFileOption = false
}: EntityBulkUploadProps) => {
  const classes                                 = useStyles();
  const entitySingleLabel                       = translate(`common:entityLabels.${entity}`);

  const existingImportStatus                    = formState[BulkSelectEntitiesFormFields.File]?.value?.importStatus;
  const existingFileIdentifiers                 = formState[BulkSelectEntitiesFormFields.EntityIdentifiers]?.value;

  const [isBinDisabled, setIsBinDisabled]       = useState<boolean>(!!existingImportStatus);
  const [isImportDisabled, setIsImportDisabled] = useState<boolean>(!!existingFileIdentifiers);
  const [identifierValue, setIdentifierValue]   = useState<string>('');

  const onEntityIdentifierListChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist();
    const {value}: {value: string} = e.target;
    setIdentifierValue(value);
    const identifiers = parsePastedIds(value);
    const validIdentifiers = [];
    identifiers.forEach(id => {
      if (id.trim()) {
        validIdentifiers.push(String(id));
      }
    });
    handleChooserChange({
      name     : BulkSelectEntitiesFormFields.EntityIdentifiers,
      value    : identifiers,
      required : true,
      errors   : false
    });
    setIsImportDisabled(!!value?.length);
  }, [identifierValue]);

  const getDefaultIdentifiers = () => {
    let identifiersString = '';
    formState[BulkSelectEntitiesFormFields.EntityIdentifiers]?.value?.forEach((identifier) => {
      identifiersString += `${identifier}\n`;
    });
    return identifiersString;
  };

  const {dialogBulkAction} = translate('common:editors.bulkActions', {returnObjects: true, entity: entitySingleLabel});

  return (
    <div className={classes.container}>
      {showFileOption && (
        <BulkFileUpload bulkUploadContainerId={BULK_UPLOAD_CONTAINER_ID}
          isImportDisabled={isImportDisabled}
          entity={entity}
          isBulkAction={isBulkAction}
          formState={formState}
          setFormState={setFormState}
          setIsBinDisabled={setIsBinDisabled}
          setIsImportDisabled={setIsImportDisabled} />
      )}

      <div className={classes.listContainer}>
        <FormItem fullWidth>
          <FormTextField autoComplete='off'
            multiline
            rows={9}
            disabled={isBinDisabled}
            hasError={formState[BulkSelectEntitiesFormFields.EntityIdentifiers]?.error}
            InputProps={{
              classes : {
                underline : !formState[BulkSelectEntitiesFormFields.EntityIdentifiers]?.error && classes.textFieldHeight
              }
            }}
            defaultValue={getDefaultIdentifiers()}
            errorText={dialogBulkAction[showFileOption ? 'identifierError' : 'pastedIdentifiersOnlyMessage']}
            fieldLabel={dialogBulkAction.pasteBinLabel}
            placeholder={dialogBulkAction.pasteBinPlaceholder?.toUpperCase()}
            onChange={onEntityIdentifierListChange} />
        </FormItem>
      </div>
    </div>
  );
};

const BULK_UPLOAD_CONTAINER_ID = 'entity-importer';

export default EntityBulkUpload;
